import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { CheckSquare, Plus, Check, X, Trash2, Pencil, Clock, Search, Filter, Calendar, User, Briefcase, AlertTriangle, Circle, CheckCircle2, XCircle } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { useAuth } from "../contexts/AuthContext";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import SearchSelect from "../components/SearchSelect";
import { toast } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORIDADE_CONFIG = {
  baixa:   { labelKey: "tarefas.low",    color: "bg-slate-100 text-slate-600" },
  media:   { labelKey: "tarefas.medium", color: "bg-blue-100 text-blue-700" },
  alta:    { labelKey: "tarefas.high",   color: "bg-amber-100 text-amber-700" },
  urgente: { labelKey: "tarefas.urgent", color: "bg-rose-100 text-rose-700" },
};

const STATUS_CONFIG = {
  aberta:    { labelKey: "tarefas.open",      icon: Circle,       color: "text-slate-400" },
  concluida: { labelKey: "tarefas.done",      icon: CheckCircle2, color: "text-emerald-500" },
  cancelada: { labelKey: "tarefas.cancelled", icon: XCircle,      color: "text-slate-300" },
};

import { formatDateTime, formatDate as _fmtDateOnly } from "../lib/dateUtils";

function fmtDate(iso) {
  if (!iso) return "—";
  return formatDateTime(iso);
}
function fmtDateShort(iso) {
  if (!iso) return "";
  return _fmtDateOnly(iso);
}
function isOverdue(iso) {
  if (!iso) return false;
  return new Date(iso) < new Date() ;
}

export default function TarefasPage() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterView, setFilterView] = useState("minhas"); // minhas | todas | vencidas
  const [filterStatus, setFilterStatus] = useState("aberta");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [concludeTarget, setConcludeTarget] = useState(null);
  const [concludeResultado, setConcludeResultado] = useState("");
  const [form, setForm] = useState({ titulo: "", tipo: "", descricao: "", responsavel_id: "", negocio_id: "", negocio_titulo: "", data_vencimento: "", prioridade: "media" });
  const appearance = useAppearance();
  const { user } = useAuth();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";

  const translateType = (name) => {
    if (!name) return name;
    const key = name.toLowerCase()
      .replace(/-/g, '_').replace(/ã/g, 'a').replace(/ç/g, 'c')
      .replace(/[êé]/g, 'e').replace(/â/g, 'a').replace(/õ/g, 'o').replace(/\s/g, '_');
    const tr = t(`taskTypes.${key}`);
    return tr !== `taskTypes.${key}` ? tr : name;
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterView === "minhas") params.set("minhas", "true");
      if (filterView === "vencidas") params.set("vencidas", "true");
      if (filterStatus && filterView !== "vencidas") params.set("status", filterStatus);
      const [tasksRes, typesRes, respRes, negRes] = await Promise.all([
        axios.get(`${API}/tasks?${params}`, { withCredentials: true }),
        axios.get(`${API}/task-types`, { withCredentials: true }),
        axios.get(`${API}/users/responsaveis`, { withCredentials: true }),
        axios.get(`${API}/negocios`, { withCredentials: true }),
      ]);
      setTasks(tasksRes.data);
      setTaskTypes(typesRes.data);
      setResponsaveis(respRes.data);
      setNegocios(negRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterView, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setEditTask(null);
    setForm({ titulo: "", tipo: taskTypes[0]?.name || "", descricao: "", responsavel_id: user?.user_id || "", negocio_id: "", negocio_titulo: "", data_vencimento: "", prioridade: "media" });
    setDialogOpen(true);
  };
  const openEdit = (task) => {
    setEditTask(t);
    setForm({ titulo: task.titulo, tipo: task.tipo, descricao: task.descricao || "", responsavel_id: task.responsavel_id || "", negocio_id: task.negocio_id || "", negocio_titulo: task.negocio_titulo || "", data_vencimento: task.data_vencimento ? task.data_vencimento.slice(0, 16) : "", prioridade: task.prioridade || "media" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) { toast.error(t("tarefas.titleRequired")); return; }
    try {
      const payload = { ...form, data_vencimento: form.data_vencimento ? new Date(form.data_vencimento).toISOString() : "" };
      if (editTask) {
        await axios.put(`${API}/tasks/${editTask.id}`, payload, { withCredentials: true });
        toast.success(t("tarefas.updated"));
      } else {
        await axios.post(`${API}/tasks`, payload, { withCredentials: true });
        toast.success(t("tarefas.created"));
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(t("tarefas.saveError")); }
  };

  const handleConclude = async () => {
    try {
      await axios.patch(`${API}/tasks/${concludeTarget.id}/concluir`, { resultado: concludeResultado }, { withCredentials: true });
      toast.success(t("tarefas.concludeSuccess"));
      setConcludeTarget(null); setConcludeResultado("");
      fetchAll();
    } catch (err) { toast.error(t("tarefas.concludeError")); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tasks/${deleteTarget.id}`, { withCredentials: true });
      setDeleteTarget(null); fetchAll();
    } catch (err) { toast.error(t("tarefas.deleteError")); }
  };

  const filtered = tasks.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return task.titulo?.toLowerCase().includes(s) || task.negocio_titulo?.toLowerCase().includes(s) || task.tipo?.toLowerCase().includes(s);
  });

  return (
    <div data-testid="tarefas-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <CheckSquare className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">{t("tarefas.title")} <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{tasks.length}</Badge></h1>
            <p className="text-slate-500 mt-0.5">{t("tarefas.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("tarefas.search")} className="pl-9 w-full sm:w-48 h-9 text-sm" />
          </div>
          <Tooltip><TooltipTrigger asChild><Button onClick={openNew} size="icon" style={{ backgroundColor: tc500 }} className="text-white h-9 w-9 hover:opacity-90"><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("tarefas.new")}</TooltipContent></Tooltip>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit flex-wrap">
        {[{ key: "minhas", label: t("tarefas.mine") }, { key: "todas", label: t("tarefas.all") }, { key: "vencidas", label: t("tarefas.overduePlural") }].map(tab => (
          <button key={tab.key} onClick={() => setFilterView(tab.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${filterView === tab.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            style={filterView === tab.key ? { color: tc500 } : undefined}>
            {tab.label}
          </button>
        ))}
        {filterView !== "vencidas" && (
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-7 w-32 text-xs border-0 bg-transparent ml-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="aberta">{t("tarefas.open")}s</SelectItem>
              <SelectItem value="concluida">{t("ui.concluidas")}</SelectItem>
              <SelectItem value="cancelada">{t("ui.canceladas")}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Task List */}
      <div className="space-y-2">
        {loading ? (
          [...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white rounded-lg border border-slate-200 animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white rounded-lg border border-slate-200">
            <CheckSquare className="w-10 h-10 mb-3 text-slate-300" />
            <p className="text-sm">{t("tarefas.empty")}</p>
          </div>
        ) : filtered.map(task => {
          const overdue = task.status === "aberta" && isOverdue(task.data_vencimento);
          const StatusIcon = STATUS_CONFIG[task.status]?.icon || Circle;
          return (
            <div key={task.id} data-testid={`task-card-${task.id}`}
              className={`bg-white rounded-lg border shadow-sm p-4 flex items-center gap-3 transition-colors ${overdue ? "border-rose-200 bg-rose-50/30" : "border-slate-200 hover:border-slate-300"}`}>
              {/* Status icon */}
              <button onClick={() => task.status === "aberta" ? setConcludeTarget(task) : null}
                className={`flex-shrink-0 transition-colors ${task.status === "aberta" ? "hover:text-emerald-500 cursor-pointer" : "cursor-default"}`}>
                <StatusIcon className={`w-5 h-5 ${STATUS_CONFIG[task.status]?.color || "text-slate-400"}`} />
              </button>

              {/* Content */}
              <div className="flex-1 min-w-0" onClick={() => openEdit(task)}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-sm font-semibold ${task.status === "concluida" ? "line-through text-slate-400" : "text-slate-900"} cursor-pointer hover:underline`}>{task.titulo}</span>
                  {task.tipo && <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">{task.tipo}</Badge>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORIDADE_CONFIG[task.prioridade]?.color || ""}`}>{t(PRIORIDADE_CONFIG[task.prioridade]?.labelKey || "")}</span>
                  {overdue && <span className="flex items-center gap-1 text-xs text-rose-600 font-medium"><AlertTriangle className="w-3 h-3" />{t("tarefas.overdue")}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                  {task.data_vencimento && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDateShort(task.data_vencimento) || t("tarefas.noLinked")}</span>}
                  {task.responsavel_nome && <span className="flex items-center gap-1"><User className="w-3 h-3" />{task.responsavel_nome}</span>}
                  {task.negocio_titulo && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{task.negocio_titulo}</span>}
                </div>
                {task.descricao && <p className="text-xs text-slate-400 mt-1 truncate">{task.descricao}</p>}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {task.status === "aberta" && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => setConcludeTarget(task)} className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 h-7 w-7 p-0"><Check className="w-3.5 h-3.5" /></Button>
                  </TooltipTrigger><TooltipContent>{t("tarefas.conclude")}</TooltipContent></Tooltip>
                )}
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(task)} className="text-sky-600 hover:text-sky-700 hover:bg-sky-50 h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                </TooltipTrigger><TooltipContent>{t("common.edit")}</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(task)} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TooltipTrigger><TooltipContent>{t("common.delete")}</TooltipContent></Tooltip>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CheckSquare className="w-5 h-5" style={{ color: tc500 }} />
              {editTask ? t("tarefas.editTitle") : t("tarefas.new")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-slate-700">{t("negocios.titleField")} <span className="text-rose-500">*</span></Label>
              <Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder={t("tarefas.titlePlaceholder")} required /></div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700">Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue placeholder={t("tarefas.selectType")} /></SelectTrigger>
                  <SelectContent>{taskTypes.map(tt => <SelectItem key={tt.id} value={tt.name}>{translateType(tt.name)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-slate-700">Prioridade</Label>
                <Select value={form.prioridade} onValueChange={v => setForm({ ...form, prioridade: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">{t("tarefas.low")}</SelectItem>
                    <SelectItem value="media">{t("tarefas.medium")}</SelectItem>
                    <SelectItem value="alta">{t("tarefas.high")}</SelectItem>
                    <SelectItem value="urgente">{t("tarefas.urgent")}</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>

            <div><Label className="text-slate-700">{t("tarefas.dueDate")}</Label>
              <Input type="datetime-local" value={form.data_vencimento} onChange={e => setForm({ ...form, data_vencimento: e.target.value })} /></div>

            <div><Label className="text-slate-700">{t("ui.responsavel")}</Label>
              <SearchSelect items={responsaveis.map(r => ({ id: r.user_id, nome: r.name, email: r.email }))} value={form.responsavel_id}
                onChange={v => setForm({ ...form, responsavel_id: v })} placeholder={t("tarefas.selectResponsavel")} emptyText={t("ui.noUsers")}
                renderItem={r => <div><span className="text-sm font-medium text-slate-800">{r.nome}</span>{r.email && <p className="text-xs text-slate-400">{r.email}</p>}</div>} /></div>

            <div><Label className="text-slate-700">{t("tarefas.negocioLinked")}</Label>
              <SearchSelect items={negocios.map(n => ({ id: n.id, nome: n.titulo, empresa_nome: n.empresa_nome }))} value={form.negocio_id}
                onChange={v => { const n = negocios.find(x => x.id === v); setForm({ ...form, negocio_id: v, negocio_titulo: n?.titulo || "" }); }}
                placeholder={t("tarefas.linkNegocio")} emptyText={t("tarefas.noNegocio")}
                renderItem={n => <div><span className="text-sm font-medium text-slate-800">{n.nome}</span>{n.empresa_nome && <p className="text-xs text-slate-400">{n.empresa_nome}</p>}</div>} /></div>

            <div><Label className="text-slate-700">{t("common.description")}</Label>
              <Textarea value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} placeholder={t("tarefas.descriptionPh")} rows={3} /></div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}><X className="w-4 h-4 mr-2" />{t("common.cancel")}</Button>
              <Button type="submit" className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }}><Check className="w-4 h-4 mr-2" />{editTask ? t("common.save") : t("tarefas.new")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Conclude Dialog */}
      <Dialog open={!!concludeTarget} onOpenChange={() => setConcludeTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" />{t("tarefas.completeTask")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 mb-3">{t("tarefas.taskLabel")} <strong>{concludeTarget?.titulo}</strong></p>
          <div><Label className="text-slate-700">{t("tarefas.resultOptional")}</Label>
            <Textarea value={concludeResultado} onChange={e => setConcludeResultado(e.target.value)} placeholder={t("tarefas.whatWasDone")} rows={3} /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setConcludeTarget(null)}><X className="w-4 h-4 mr-2" />{t("common.cancel")}</Button>
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConclude}><Check className="w-4 h-4 mr-2" />{t("tarefas.conclude")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)} title={t("tarefas.deleteTitle")} description={t("tarefas.deleteConfirm", { titulo: deleteTarget?.titulo })} onConfirm={handleDelete} />
    </div>
  );
}
